import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  ArrowSquareOut,
  FloppyDisk,
  ArrowCounterClockwise,
  ShieldCheck,
  CheckCircle,
} from '@phosphor-icons/react';
import { apiClient, API_ENDPOINTS, useAuthStore } from '@dukene/shared';

const POLICIES = [
  {
    key: 'privacyPolicy',
    label: 'Privacy Policy',
    path: '/policies/privacy-policy',
    description: 'Required by Google Merchant Center. Explains how customer data is collected, processed, and protected.',
    placeholder: `We collect your name, email, and shipping information to process orders and send updates.

We do not share your personal information with third parties except as needed to deliver your order.

You may contact us at any time to request access to, correction of, or deletion of your data.`,
  },
  {
    key: 'refundPolicy',
    label: 'Refund & Return Policy',
    path: '/policies/refund-policy',
    description: 'Required by Google Merchant Center. Explains your return eligibility, exchange timeframe, and refund timeline.',
    placeholder: `We accept returns within 14 days of delivery. Items must be in their original condition.

To start a return, please contact us with your order number.

Refunds are processed within 5–10 business days after we receive the returned item.`,
  },
  {
    key: 'termsOfService',
    label: 'Terms of Service',
    path: '/policies/terms-of-service',
    description: 'Defines rules for accessing your online storefront, ordering products, and dispute resolutions.',
    placeholder: `By placing an order on our store, you agree to these terms.

Prices and availability are subject to change without notice.

We reserve the right to refuse service to anyone.`,
  },
  {
    key: 'shippingPolicy',
    label: 'Shipping & Fulfillment Policy',
    path: '/policies/shipping-policy',
    description: 'Required by Google Merchant Center. Describes processing times, carriers, and delivery windows.',
    placeholder: `Orders are processed within 1–3 business days.

Standard delivery takes 2–5 business days depending on your location.

You will receive a tracking notification once your order has shipped.`,
  },
] as const;

type PolicyKey = (typeof POLICIES)[number]['key'];

interface StoreSettings {
  privacyPolicy?: string;
  refundPolicy?: string;
  termsOfService?: string;
  shippingPolicy?: string;
  [key: string]: unknown;
}

interface StoreData {
  id: string;
  subdomain: string;
  name: string;
  settings: StoreSettings | null;
}

export default function PoliciesSettings() {
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const [drafts, setDrafts] = useState<Partial<Record<PolicyKey, string>>>({});
  const [dirtyKeys, setDirtyKeys] = useState<Set<PolicyKey>>(new Set());
  const [activeTab, setActiveTab] = useState<PolicyKey>('privacyPolicy');

  const { data: store, isLoading } = useQuery<StoreData>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: () => apiClient.get<StoreData>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!activeStoreId,
  });

  const mutation = useMutation({
    mutationFn: (settings: Partial<Record<PolicyKey, string>>) =>
      apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, { settings }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-store', activeStoreId] });
      setDirtyKeys(new Set());
      setDrafts({});
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#006838] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const currentSettings = (store?.settings || {}) as StoreSettings;
  const storeSubdomain = store?.subdomain || 'your-store';

  const getValue = (key: PolicyKey): string =>
    dirtyKeys.has(key) ? (drafts[key] ?? '') : (currentSettings[key] ?? '');

  const handleChange = (key: PolicyKey, value: string) => {
    setDrafts((d) => ({ ...d, [key]: value }));
    setDirtyKeys((s) => new Set(s).add(key));
  };

  const handleReset = (key: PolicyKey) => {
    setDrafts((d) => {
      const n = { ...d };
      delete n[key];
      return n;
    });
    setDirtyKeys((s) => {
      const n = new Set(s);
      n.delete(key);
      return n;
    });
  };

  const handleSave = () => {
    const updates: Partial<Record<PolicyKey, string>> = {};
    dirtyKeys.forEach((k) => {
      updates[k] = drafts[k] ?? '';
    });
    mutation.mutate(updates);
  };

  const activePolicy = POLICIES.find((p) => p.key === activeTab)!;

  return (
    <div className="space-y-4 pb-16">
      {/* High-density Polaris Admin Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E1E4E8] dark:bg-slate-800 flex items-center justify-center text-[#202223] dark:text-slate-200">
              <ShieldCheck weight="fill" className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-semibold text-[#202223] dark:text-white">Store Legal Policies & Compliance</h1>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
                  <CheckCircle weight="fill" className="w-3 h-3" /> Google Shopping Compliant
                </span>
              </div>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                Configure your public compliance policies required by Google Merchant Center and Meta Catalog
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Split */}
      <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-4">
        {/* Policy Tab List */}
        <nav className="flex flex-col sm:flex-row lg:flex-col gap-1.5">
          {POLICIES.map((p) => {
            const isDirty = dirtyKeys.has(p.key);
            const hasSaved = !!currentSettings[p.key];
            const active = activeTab === p.key;

            return (
              <button
                type="button"
                key={p.key}
                onClick={() => setActiveTab(p.key)}
                className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                  active
                    ? 'bg-[#E1E4E8] dark:bg-slate-800 border-[#C9CCCF] dark:border-slate-700 text-[#202223] dark:text-white font-semibold'
                    : 'bg-white dark:bg-[#1A1D21] border-[#D2D5D9] dark:border-slate-800 text-[#5C5F62] dark:text-slate-400 hover:bg-[#F6F6F7]'
                }`}
              >
                <div className="flex items-center gap-2.5 truncate">
                  <FileText weight="fill" className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{p.label}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isDirty && <span className="w-2 h-2 rounded-full bg-amber-500" title="Unsaved changes" />}
                  {!isDirty && hasSaved && <span className="w-2 h-2 rounded-full bg-[#006838]" title="Customized" />}
                </div>
              </button>
            );
          })}
        </nav>

        {/* Policy Editor Container */}
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-[#202223] dark:text-white">{activePolicy.label}</h3>
                {currentSettings[activePolicy.key] ? (
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-[#E3F1DF] text-[#006838]">
                    Custom Policy
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-[#E1E4E8] dark:bg-slate-800 text-[#5C5F62]">
                    Default Template
                  </span>
                )}
              </div>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">{activePolicy.description}</p>
            </div>

            <a
              href={`https://${storeSubdomain}.dukene.com${activePolicy.path}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#202223] dark:text-slate-200 hover:bg-[#F6F6F7]"
            >
              <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
              Preview Live Page
            </a>
          </div>

          {!currentSettings[activePolicy.key] && !dirtyKeys.has(activePolicy.key) && (
            <div className="p-3.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs text-amber-800 dark:text-amber-300">
              <strong>Using Platform Default Template:</strong> Your storefront displays our attorney-reviewed standard policy template. Edit below to override with custom policy language.
            </div>
          )}

          <div>
            <textarea
              value={getValue(activePolicy.key)}
              onChange={(e) => handleChange(activePolicy.key, e.target.value)}
              placeholder={activePolicy.placeholder}
              rows={16}
              className="w-full p-3.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono leading-relaxed text-[#202223] dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-[#006838] resize-none"
              spellCheck
            />
          </div>

          <div className="flex items-center justify-between text-xs text-[#5C5F62] dark:text-slate-400 pt-1">
            <span>{getValue(activePolicy.key).length.toLocaleString()} characters</span>
            {dirtyKeys.has(activePolicy.key) && (
              <button
                type="button"
                onClick={() => handleReset(activePolicy.key)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 hover:text-amber-700"
              >
                <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5" />
                Revert Changes
              </button>
            )}
          </div>

          <div className="p-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 text-xs text-[#5C5F62]">
            Storefront URL: <code className="font-mono text-[#202223] dark:text-slate-200">https://{storeSubdomain}.dukene.com{activePolicy.path}</code>
          </div>
        </div>
      </div>

      {/* Sticky Save Bar */}
      {dirtyKeys.size > 0 && (
        <div className="sticky bottom-4 z-30 flex items-center justify-between px-4 py-3 rounded-lg bg-[#202223] text-white shadow-md border border-slate-700">
          <div className="flex items-center gap-2.5">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs font-medium">You have unsaved edits to your legal policies ({dirtyKeys.size} modified).</span>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded bg-[#006838] hover:bg-[#00522c] text-white transition-colors disabled:opacity-50"
          >
            <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
            {mutation.isPending ? 'Saving...' : 'Save Legal Policies'}
          </button>
        </div>
      )}
    </div>
  );
}
